import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useRouter } from "next/router"

import {
  buildSofiaNotebooksPath,
  projectNotebookCells,
  useCovi,
  useSetSofiaThreadHeaderAction,
  useSofiaPanelContent,
} from "@/app-covi"
import { Button } from "@coval/ui"
import { NotebookPen } from "lucide-react"

import {
  appendNotebookReceipt,
  isNotebookConversionReceipt,
} from "../utils/notebookReceipt"
import { createNotebook } from "../utils/notebooksApi"
import {
  buildFittingNotebookSnapshot,
  buildNotebookTranscriptMessages,
  defaultNotebookTitle,
} from "../utils/notebookSnapshot"

interface ChatNotebookActionProps {
  workspaceId: string
  organizationSlug: string
}

export function ChatNotebookAction({
  workspaceId,
  organizationSlug,
}: ChatNotebookActionProps) {
  const router = useRouter()
  const {
    activeThreadId,
    isOpen,
    threads,
    loadThreadItems,
    appendThreadItems,
    persistThreadItems,
  } = useCovi()
  const panelContent = useSofiaPanelContent()
  const pendingRef = useRef(new Set<string>())
  const [pending, setPending] = useState<ReadonlySet<string>>(() => new Set())
  const scope = `${organizationSlug}:${workspaceId}`
  const activeScope = useRef({ scope, generation: 0 })
  const threadIds = useRef(new Set(threads.map((thread) => thread.id)))
  useEffect(() => {
    threadIds.current = new Set(threads.map((thread) => thread.id))
  }, [threads])
  const navigationGeneration = useRef(0)
  useEffect(() => {
    navigationGeneration.current += 1
  }, [activeThreadId, isOpen, panelContent])
  useEffect(() => {
    const markNavigation = () => {
      navigationGeneration.current += 1
    }
    router.events.on("routeChangeStart", markNavigation)
    router.events.on("hashChangeStart", markNavigation)
    return () => {
      router.events.off("routeChangeStart", markNavigation)
      router.events.off("hashChangeStart", markNavigation)
    }
  }, [router.events])
  useEffect(() => {
    activeScope.current = {
      scope,
      generation: activeScope.current.generation + 1,
    }
    return () => {
      activeScope.current = {
        scope: "",
        generation: activeScope.current.generation + 1,
      }
    }
  }, [scope])
  const save = useCallback(
    async (disabled: boolean) => {
      const requestKey = `${scope}:${activeThreadId}`
      if (disabled || !activeThreadId || pendingRef.current.has(requestKey)) {
        return
      }
      const generation = activeScope.current.generation
      const navigationAtStart = navigationGeneration.current
      const originPath = router.asPath
      const threadId = activeThreadId
      const transcript = loadThreadItems(threadId)
      const capturedItems = JSON.stringify(transcript)
      const items = transcript.filter(
        (item) => !isNotebookConversionReceipt(item),
      )
      const cells = projectNotebookCells(items)
      const receipt = (text: string, kind: "assistant" | "error") =>
        appendNotebookReceipt(
          { appendThreadItems, loadThreadItems, persistThreadItems },
          threadId,
          { kind, id: `notebook_${Date.now().toString(36)}`, text },
        )
      const snapshot = buildFittingNotebookSnapshot((options) =>
        projectNotebookCells(items, options),
      )
      if (!snapshot) {
        receipt("This chat is too large to save as a notebook.", "error")
        return
      }
      pendingRef.current.add(requestKey)
      setPending(new Set(pendingRef.current))
      try {
        const notebook = await createNotebook({
          organizationSlug,
          workspaceId,
          title: defaultNotebookTitle(cells),
          mode: "chat",
          snapshot,
          messages: buildNotebookTranscriptMessages(items),
        })
        const href = buildSofiaNotebooksPath(
          originPath,
          organizationSlug,
          notebook.id,
        )
        if (
          activeScope.current.scope !== scope ||
          activeScope.current.generation !== generation ||
          !threadIds.current.has(threadId)
        ) {
          return
        }
        const chatChanged =
          JSON.stringify(loadThreadItems(threadId)) !== capturedItems
        receipt(
          chatChanged
            ? `Created **${notebook.title}** from the chat as it was when you started creating the notebook. Newer messages are not included. [Open notebook](${href})`
            : `Saved this chat as the notebook **${notebook.title}**. [Open notebook](${href})`,
          "assistant",
        )
        if (
          !chatChanged &&
          navigationGeneration.current === navigationAtStart
        ) {
          void router.push(href)
        }
      } catch (error: unknown) {
        if (
          activeScope.current.scope !== scope ||
          activeScope.current.generation !== generation ||
          !threadIds.current.has(threadId)
        ) {
          return
        }
        receipt(
          error instanceof Error
            ? error.message
            : "Could not save the notebook.",
          "error",
        )
      } finally {
        pendingRef.current.delete(requestKey)
        if (activeScope.current.scope) {
          setPending(new Set(pendingRef.current))
        }
      }
    },
    [
      activeThreadId,
      appendThreadItems,
      loadThreadItems,
      organizationSlug,
      persistThreadItems,
      router,
      scope,
      workspaceId,
    ],
  )

  const saving = pending.has(`${scope}:${activeThreadId}`)
  const headerAction = useMemo(
    () =>
      activeThreadId
        ? ({ disabled }: { disabled: boolean }) => (
            <Button
              variant="ghost"
              icon
              tooltip="Turn into notebook"
              onClick={() => void save(disabled)}
              loading={saving}
              disabled={disabled}
            >
              <NotebookPen aria-hidden="true" />
            </Button>
          )
        : null,
    [activeThreadId, save, saving],
  )
  useSetSofiaThreadHeaderAction(headerAction)
  return null
}
